import {
  AppLayout,
  AuthGuard,
  BackendUnavailableOverlay,
  backendStatusAtom,
  domainCodeAtom,
  SaladBarProvider,
  titleTextAtom,
  topBarHeightAtom,
} from '@australiangreens/ag-internal-components';
import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions,
  CacheLocation,
  useAuth0,
  User,
} from '@auth0/auth0-react';
import { Box, Link } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import Auth0SessionController from '@components/Auth0SessionController';
import NavBarBottomContent, { NavBarMiddleContent } from '@components/NavBarContent';
import {
  timeoutPeriodAtom,
  useAppAsIFrame,
  useSetBackendGitCommitHash,
  useUpdateUserPicture,
} from '@hooks/useApp';
import { usePrefetchDefaultSearchTree } from '@pages/AdvancedSearchListCreatorPage/JsonTreeBuilder';
import { usePrefetchQuickFilter } from '@pages/QuickFilterListCreatorPage/Loader';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import AppRoutes from './AppRoutes';
import { callBackendApiWithoutAuth } from './apis/call';
import { useAccessToken } from '@hooks/useAccessToken';
import { startCiviRecoveryPolling } from './util/civiRecovery';
import { startDbRecoveryPolling } from './util/dbRecovery';
import { startRedisRecoveryPolling } from './util/redisRecovery';
import { shouldRetryQuery } from './util/shouldRetryQuery';
import { usePrefetchSearch } from './apis/searches';
import {
  auth0Audience,
  auth0ClientId,
  auth0Domain,
  isReactQueryDevtoolsEnabled,
  isSentryDisabled,
} from './config';
import { FeatureToggleProvider } from './featureToggles/FeatureToggleProvider';
import { usePrefetchPathwaySteps, useUserDetails } from './hooks';

export const NotRenewingContext = createContext<{
  notRenewingPresent: boolean;
  setNotRenewingPresent: React.Dispatch<React.SetStateAction<boolean>>;
}>({ notRenewingPresent: false, setNotRenewingPresent: () => {} } as unknown as {
  notRenewingPresent: boolean;
  setNotRenewingPresent: React.Dispatch<React.SetStateAction<boolean>>;
});

const SkipToMainContentLink = () => (
  <Box
    sx={{
      maxHeight: '0',
      ':focus-within': { maxHeight: '5rem', paddingY: '1rem' },
      transition: 'all 0.25s',
      overflow: 'hidden',
      flexShrink: 0,
      paddingX: '1rem',
      left: '5rem',
      top: '5rem',
      position: 'absolute',
    }}
  >
    <Link href="#main-content">Skip to main content</Link>
  </Box>
);

const App = () => {
  const storedDomainCode = useAtomValue(domainCodeAtom);
  const { user } = useAuth0();
  const { data: userDetails } = useUserDetails();

  useUpdateUserPicture();
  useSetBackendGitCommitHash();
  useAppAsIFrame();

  usePrefetchPathwaySteps();
  usePrefetchSearch(NaN);
  usePrefetchDefaultSearchTree();
  usePrefetchQuickFilter(null, undefined);

  const topBarHeight = useAtomValue(topBarHeightAtom);

  const setTitle = useSetAtom(titleTextAtom);

  useEffect(() => {
    setTitle('List Manager');
  }, [setTitle]);

  const [notRenewingPresent, setNotRenewingPresent] = useState(false);

  return (
    <NotRenewingContext value={{ notRenewingPresent, setNotRenewingPresent }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh"
        }}>
        <SkipToMainContentLink />
        <AppLayout
          pageContainerProps={{ topBarHeight: topBarHeight, noPadding: true }}
          navBarMiddle={<NavBarMiddleContent userLevel={userDetails?.userLevel} />}
          initialTitleText="List Manager"
          domainCode={storedDomainCode}
          user={user}
          navBarBottom={<NavBarBottomContent />}
          useNewAvatar={true}
        >
          <Box
            tabIndex={0}
            id="main-content"
            role="main"
            aria-label="List Manager main content"
            sx={{
              height: "inherit",
              flexGrow: 1,
              overflow: "auto"
            }}>
            <AppRoutes />
          </Box>
        </AppLayout>
      </Box>
      <Auth0SessionController />
    </NotRenewingContext>
  );
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stop RQ retries while an outage is latched — cuts CORS console floods.
      // Do not pause the client: Redis recovery still invalidates → fetches.
      retry: (failureCount) => shouldRetryQuery(failureCount),
    },
  },
});

/** Auth0 callback query shape (code|error + state) — same idea as auth0-react's hasAuthParams. */
const isAuth0RedirectCallback = (search = window.location.search) =>
  (/[?&](?:code|error)=/.test(search) && /[?&]state=/.test(search));

/** Overlay + Redis/Civi/DB recovery — must render under Auth0Provider. */
const BackendOutageHost = () => {
  const backendStatus = useAtomValue(backendStatusAtom);
  const storedDomainCode = useAtomValue(domainCodeAtom);
  const getAccessToken = useAccessToken();

  const probeHealth = useCallback(
    () => callBackendApiWithoutAuth({ method: 'get', endpoint: '/hashz' }),
    []
  );

  const onRecover = useCallback(() => {
    void queryClient.invalidateQueries();
  }, []);

  // Cancel in-flight retries as soon as an outage latches (retry:false alone
  // does not abort attempts already scheduled).
  useEffect(() => {
    if (backendStatus.status === 'DOWN') {
      void queryClient.cancelQueries();
    }
  }, [backendStatus.status]);

  // While Redis is latched, retry active queries so a successful authenticated
  // response can clear Redis. Search previews are excluded from that poll.
  useEffect(() => {
    if (
      backendStatus.status !== 'DOWN' ||
      backendStatus.reason !== 'REDIS_UNCONTACTABLE'
    ) {
      return undefined;
    }
    return startRedisRecoveryPolling(queryClient);
  }, [backendStatus.status, backendStatus.reason]);

  // While Civi is latched, probe a cheap Civi-backed GET on the shared cadence.
  useEffect(() => {
    if (
      backendStatus.status !== 'DOWN' ||
      backendStatus.reason !== 'CIVI_CRM_UNCONTACTABLE'
    ) {
      return undefined;
    }
    return startCiviRecoveryPolling({
      getAccessToken,
      domainCode: storedDomainCode,
      onRecovered: onRecover,
    });
  }, [
    backendStatus.status,
    backendStatus.reason,
    getAccessToken,
    storedDomainCode,
    onRecover,
  ]);

  // While the database is latched, probe a cheap DB-backed GET on the shared cadence.
  useEffect(() => {
    if (
      backendStatus.status !== 'DOWN' ||
      backendStatus.reason !== 'DATABASE_UNCONTACTABLE'
    ) {
      return undefined;
    }
    return startDbRecoveryPolling({
      getAccessToken,
      onRecovered: onRecover,
    });
  }, [backendStatus.status, backendStatus.reason, getAccessToken, onRecover]);

  return <BackendUnavailableOverlay probeHealth={probeHealth} onRecover={onRecover} />;
};

const AppWithAuth0 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutPeriod = useAtomValue(timeoutPeriodAtom);

  // Auth0 always returns to redirect_uri (the origin root). Hold the UI on a
  // skeleton until the router location matches returnTo, so Home does not flash.
  // '' = callback in progress (target unknown yet); null = not awaiting.
  const [pendingReturnTo, setPendingReturnTo] = useState<string | null>(() =>
    isAuth0RedirectCallback() ? '' : null
  );
  const currentPath = `${location.pathname}${location.search}`;
  if (pendingReturnTo !== null && pendingReturnTo !== '' && currentPath === pendingReturnTo) {
    setPendingReturnTo(null);
  }
  const awaitingPostLoginNavigation = pendingReturnTo !== null;

  const isLocal = window.location.hostname === 'localhost';
  const cacheLocationProps = isLocal
    ? { cacheLocation: 'localstorage' as CacheLocation } // Use localstorage to persist login without refresh tokens
    : ({} as Partial<Auth0ProviderOptions<User>>);

  // AuthGuard already stores the deep link in appState.returnTo; Auth0's default
  // callback only does history.replaceState, which React Router does not observe.
  const onRedirectCallback = (appState?: AppState) => {
    const returnTo = appState?.returnTo ?? window.location.pathname;
    navigate(returnTo, { replace: true });
    setPendingReturnTo(returnTo);
  };

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{ redirect_uri: window.location.origin, audience: auth0Audience }}
      useRefreshTokens={!isLocal}
      useRefreshTokensFallback={true}
      onRedirectCallback={onRedirectCallback}
      {...cacheLocationProps}
    >
      <AuthGuard appName="List Manager" throwErrors="unknown">
        {awaitingPostLoginNavigation ? (
          <Skeleton variant="rectangular" animation="pulse" height="100vh" width="100vw" />
        ) : (
          <QueryClientProvider client={queryClient}>
            <FeatureToggleProvider>
              <SaladBarProvider autoHideDuration={timeoutPeriod * 1000}>
                {/* Civi/Redis/backend outages use BackendUnavailableOverlay only —
                    do not mount ServerStatusErrorBoundary (it replaces the page).
                    SaladBarProvider allows only a single child. */}
                <>
                  <App />
                  <BackendOutageHost />
                </>
              </SaladBarProvider>
              {isReactQueryDevtoolsEnabled && (
                <ReactQueryDevtools initialIsOpen={false} position="bottom" />
              )}
            </FeatureToggleProvider>
          </QueryClientProvider>
        )}
      </AuthGuard>
    </Auth0Provider>
  );
};

const defaultExport = isSentryDisabled ? AppWithAuth0 : Sentry.withProfiler(AppWithAuth0);

export default defaultExport;
